import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Radio, Table, TableColumnsType, Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { uniqueId } from 'lodash';
import {
  getImages,
  uploadFarmerImage,
  uploadCommunityImage,
  uploadOperationImage,
  uploadGapImage
} from '@/adapters/master';
import { IUploadImageRes } from '@/interfaces/master';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { refNo?: string; errors?: { errorMessage?: string } };
}

// Updated Type to include new categories
type Category = 'farmer_upload' | 'community_upload' | 'operation_upload' | 'crop_upload';

const getErrorMessage = (r: unknown): string => {
  const error = r as any;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const UploadImagesPage: React.FC = () => {
  const [tab, setTab] = useState<Category>('farmer_upload');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);

  const { data, refetch, isFetching } = useQuery<IUploadImageRes, Error>({
    queryKey: ['get-images'],
    queryFn: getImages,
    retry: 0,
  });

  // Updated Keys Mapping for Operation and Crop
  const keys = useMemo(() => {
    switch (tab) {
      case 'operation_upload':
        return { id: 'OP_TRNBR', name: 'DIS_DESC1', lastName: 'OP__QAD01', pic: 'PIC_STORE' };
      case 'crop_upload':
        return { id: 'WO_LOT', name: 'PT_DESC1', lastName: 'FARM_ID', pic: 'FARM_CER_PIC' };
      case 'community_upload':
        return { id: 'SI_SITE', name: 'SI_DESC', lastName: '', pic: 'SI_PIC' };
      default: // farmer_upload
        return { id: 'FRM_CODE', name: 'FRM_FNAME', lastName: 'FRM_LNAME', pic: 'FRM_PF' };
    }
  }, [tab]);

  useEffect(() => {
    const list = data?.[tab] || [];
    setSelected((list as any[]).map((item: any) => ({ ...item, _key: uniqueId('img-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const showSystemError = (err: any) => {
    const msg = getErrorMessage(err);
    notification.error({
      key: 'sync_status_msg',
      message: 'ไม่สามารถติดต่อ Server ได้ (System Error)',
      style: { width: '550px' },
      description: <pre style={{ fontSize: '11px', background: '#fff1f0', padding: '10px', border: '1px solid #ffa39e', borderRadius: '4px', whiteSpace: 'pre-wrap', color: '#820014' }}>{msg}</pre>,
      duration: 0,
    });
  };

  const notifyBatchErrors = (title: string, batchResults: IBatchRejection[], originalData: any[]) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const item = originalData[index];
          const failedId = result.reason?.refNo || item?.[keys.id] || `ลำดับที่ ${index + 1}`;
          const errMsg = result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ';
          errors.push(`${failedId}: ${errMsg}`);
        }
      });
    }
    if (errors.length > 0) {
      notification.error({
        key: 'sync_status_msg',
        message: title,
        style: { width: '500px' },
        description: (
          <ul style={{ paddingLeft: '15px', margin: 0, maxHeight: '250px', overflowY: 'auto' }}>
            {errors.map((err, i) => <li key={i} style={{ marginBottom: 4 }}>{err}</li>)}
          </ul>
        ),
        duration: 10,
      });
      return true;
    }
    return false;
  };

  const imageMutation = useMutation({
    mutationFn: async (payload: any[]) => {
      notification.destroy('sync_status_msg');
      // Logic to select correct API based on tab
      if (tab === 'farmer_upload') return await uploadFarmerImage(payload);
      if (tab === 'community_upload') return await uploadCommunityImage(payload);
      if (tab === 'operation_upload') return await uploadOperationImage(payload);
      if (tab === 'crop_upload') return await uploadGapImage(payload);
      throw new Error('Invalid Category');
    },
    onSuccess: (res, variables) => {
      if (res?.status === 500 || res?.errorCode === "500" || res?.message?.includes('circular')) {
        showSystemError(res);
        return;
      }
      const hasErrors = notifyBatchErrors('แก้ไขข้อมูลสำเร็จ', res?.data as IBatchRejection[], variables);
      if (!hasErrors) {
        notification.success({ key: 'sync_status_msg', message: 'Sync ข้อมูลสำเร็จ' });
      }
      refetch();
    },
    onError: (err) => showSystemError(err),
  });

  const getColumnSearchProps = (dataIndex: string) => ({
    filters: Array.from(new Set(selected.map((item) => item[dataIndex] || '-'))).filter(Boolean).map((val) => ({ text: String(val), value: String(val) })),
    filterSearch: true,
    onFilter: (value: any, record: any) => String(record[dataIndex]).toLowerCase().includes(String(value).toLowerCase()),
  });

  const columns: TableColumnsType<any> = useMemo(() => [
    { title: 'ลำดับ', width: 60, align: 'center', render: (_, __, i) => <b>{i + 1}</b> },
    {
      title: tab === 'operation_upload' ? 'Transaction No.' : tab === 'crop_upload' ? 'Lot No.' : 'รหัสอ้างอิง',
      dataIndex: keys.id,
      width: 150,
      sorter: (a, b) => String(a[keys.id]).localeCompare(String(b[keys.id])),
      ...getColumnSearchProps(keys.id)
    },
    {
      title: 'รายละเอียด / ชื่อ',
      render: (_, r) => `${r[keys.name] || ''} ${r[keys.lastName] || ''}`.trim()
    },
    { title: 'ชื่อรูปภาพ', dataIndex: keys.pic, ...getColumnSearchProps(keys.pic) },
  ], [tab, keys, selected]);

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lower = searchText.toLowerCase();
    return selected.filter((item: any) => Object.values(item).some(val => String(val).toLowerCase().includes(lower)));
  }, [selected, searchText]);

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Radio.Group value={tab} onChange={(e) => setTab(e.target.value)} buttonStyle="solid">
          <Radio.Button value="farmer_upload">Farmer</Radio.Button>
          <Radio.Button value="community_upload">Community</Radio.Button>
          <Radio.Button value="operation_upload">Operation</Radio.Button>
          <Radio.Button value="crop_upload">Crop</Radio.Button>
        </Radio.Group>
        <Flex gap={12} align="center">
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>รีเฟรช</Button>
          <Input.Search placeholder="ค้นหา..." allowClear onChange={(e) => setSearchText(e.target.value)} style={{ width: 300 }} />
          <Button
            type="primary"
            style={{ minWidth: 150, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            disabled={!selectedRowKeys.length || imageMutation.isPending}
            onClick={() => imageMutation.mutate(selectedRows)}
          >
            {imageMutation.isPending ? 'กำลังดำเนินการ...' : 'รายการแก้ไข >'}
          </Button>
        </Flex>
      </Flex>
      <Table
        rowSelection={{ selectedRowKeys, onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows); } }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 'max-content' }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default UploadImagesPage;